import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; 
import { useFontSize } from './FontSizeContext';

import { Plus, Search, User as UserIcon, Shield, ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const fontContext = useFontSize();

  const fontSize = fontContext?.fontSize || 13;
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const itemsPerPage = 15;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/users`, {
        params: { page: currentPage, limit: itemsPerPage, q: searchTerm },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(res.data.data);
      setTotalItems(res.data.total);
    } catch (err) {
      toast.error(t('userList.errLoadUsers')); // 🎯
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] font-bold uppercase tracking-tighter ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }}>
      {/* Toolbar */}
      <div className="bg-white p-3 border-b shadow-sm flex justify-between items-center">
        <div className={`flex items-center gap-6 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <h2 className="text-[#005495] text-lg italic flex items-center gap-2">
            <UserIcon size={20} /> {t('userList.title')} {/* 🎯 */}
          </h2>
          <div className="relative">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-2.5 text-gray-400`} size={14} />
            <input 
              className={`py-2 border border-gray-300 w-80 outline-none focus:border-blue-600 rounded-sm shadow-inner ${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'}`} 
              placeholder={t('userList.plcSearch')} // 🎯
              value={searchTerm}
              onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
            />
          </div>
        </div>
        <button onClick={() => navigate('/users/new')} className={`bg-[#005495] text-white px-6 py-2 rounded-sm flex items-center gap-2 hover:bg-black transition-all shadow-lg ${isRTL ? 'ml-4' : 'mr-4'}`}>
          <Plus size={16} /> {t('userList.btnNewUser')} {/* 🎯 */}
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-4">
        <div className="bg-white border-2 border-gray-200 rounded-sm shadow-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#1a3353] text-white sticky top-0 z-10">
              <tr>
                <th className={`p-3 border-white/10 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{t('userList.colUserId')}</th>
                <th className={`p-3 border-white/10 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{t('userList.colFullName')}</th>
                <th className={`p-3 border-white/10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('userList.colRole')}</th>
                <th className={`p-3 border-white/10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('userList.colSite')}</th>
                <th className={`p-3 border-white/10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('userList.colStatus')}</th>
                <th className="p-3 text-center">{t('userList.colEmail')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="6" className="p-10 text-center animate-pulse">{t('userList.msgSyncing')}</td></tr>
              ) : users.map((u) => (
                <tr key={u.user_id} className="hover:bg-blue-50 cursor-pointer" onClick={() => navigate(`/users/${u.user_id}`)}>
                  <td className={`p-3 font-black text-blue-900 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{u.username}</td>
                  <td className={`p-3 text-gray-700 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{u.full_name}</td>
                  <td className={`p-3 text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                    <span className="flex items-center justify-center gap-1 text-orange-600">
                      <Shield size={12} /> {u.role_name || t('userList.lblNoRole')}
                    </span>
                  </td>
                  <td className={`p-3 text-center text-gray-500 italic ${isRTL ? 'border-l' : 'border-r'}`}>{u.site_id || t('userList.lblAll')}</td>
                  <td className={`p-3 text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                    {u.is_active ? 
                      <CheckCircle size={14} className="text-green-600 mx-auto" /> : 
                      <XCircle size={14} className="text-red-600 mx-auto" />
                    }
                  </td>
                  <td className="p-3 text-center text-gray-400 lowercase italic">{u.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#1a3353] text-white p-2 px-8 flex justify-between items-center shadow-inner">
        <span className="text-blue-300">{t('userList.lblTotalUsers')} {totalItems}</span>
        <div className="flex items-center gap-2">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="p-1 border rounded disabled:opacity-20">
              <ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <span>{t('userList.lblPage')} {currentPage}</span>
          <button disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)} className="p-1 border rounded disabled:opacity-20">
              <ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserList;